<script setup lang="ts">
/* eslint-disable @intlify/vue-i18n/no-raw-text */

import LeButton from "@dsk/components/Button/LeButton.vue";
import LeInput from "@dsk/components/Form/LeInput.vue";
import LeSelect from "@dsk/components/Form/LeSelect.vue";
import LeHeading from "@dsk/components/Heading/LeHeading.vue";
import LeModal from "@dsk/components/LeModal.vue";
import type { ModalProps } from "@dsk/types";
import { useField, useForm } from "vee-validate";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { DictionariesHelper, ValidationHelper } from "@/helpers";
import { CompanyDictionarySuggestionsService } from "@/services";
import type { LeSelectOption } from "@/types";
import { sleep } from "@/utils";
import {
  type CompanyDanskeBankDictionarySuggestion,
  companyDanskeBankDictionarySuggestionFormValidator,
  companyDanskeBankDictionarySuggestionLanguages,
  type CompanyNordeaDictionarySuggestion,
  companyNordeaDictionarySuggestionFormValidator,
  companyNordeaDictionarySuggestionLanguages
} from "@/validation";

const props = defineProps<ModalProps & { dictionaryId: string }>();

const emit = defineEmits<{
  (event: "close", didSubmit: boolean): void;
}>();

const { t } = useI18n();

const suggestTermsDictionaryName = computed<string>(() => {
  if (props.dictionaryId === DictionariesHelper.NORDEA_TERMS_DICTIONARY_ID) {
    return "Nordea terms";
  } else if (props.dictionaryId === DictionariesHelper.DANSKE_BANK_TERMS_DICTIONARY_ID) {
    return "Danske Bank terms";
  } else {
    return "";
  }
});
const suggestTermsHeadline = computed<string>(() => {
  if (props.dictionaryId === DictionariesHelper.NORDEA_TERMS_DICTIONARY_ID) {
    return "Suggest new terms/add comments";
  } else if (props.dictionaryId === DictionariesHelper.DANSKE_BANK_TERMS_DICTIONARY_ID) {
    return "Suggest a new term or send feedback to Danske Bank";
  } else {
    return "";
  }
});
const suggestTermsCommentPlaceholder = computed<string>(() => {
  if (props.dictionaryId === DictionariesHelper.NORDEA_TERMS_DICTIONARY_ID) {
    return "Example of term in sentence/comments";
  } else if (props.dictionaryId === DictionariesHelper.DANSKE_BANK_TERMS_DICTIONARY_ID) {
    return "Comments";
  } else {
    return "";
  }
});
const suggestTermsEmailHeadline = computed<string>(() => {
  if (props.dictionaryId === DictionariesHelper.NORDEA_TERMS_DICTIONARY_ID) {
    return "About you";
  } else if (props.dictionaryId === DictionariesHelper.DANSKE_BANK_TERMS_DICTIONARY_ID) {
    return "Your Danske Bank email";
  } else {
    return "";
  }
});

const {
  handleSubmit,
  resetForm: resetFormRaw,
  errors
} = useForm({
  validationSchema: computed(() => {
    if (props.dictionaryId === DictionariesHelper.DANSKE_BANK_TERMS_DICTIONARY_ID) {
      return companyDanskeBankDictionarySuggestionFormValidator;
    }
    return companyNordeaDictionarySuggestionFormValidator;
  })
});

const { value: term } = useField<string>("term");
const { value: example } = useField<string>("example");
const { value: source, meta: sourceMeta } = useField<string>("source");
const { value: email, meta: emailMeta } = useField<string>("email");

const sourceFieldLabel = computed<string>(() => {
  if (!errors.value.source) {
    return t("validation.company-dictionary-suggestion-required");
  }

  return errors.value.source;
});

const emailFieldLabel = computed<string>(() => {
  if (!errors.value.email) {
    return t("validation.company-dictionary-suggestion-required");
  }

  return errors.value.email;
});

const sourceOptions: LeSelectOption[] = computed(() => {
  if (props.dictionaryId === DictionariesHelper.DANSKE_BANK_TERMS_DICTIONARY_ID) {
    return companyDanskeBankDictionarySuggestionLanguages.map((l) => ({ text: l, value: l }));
  }
  return companyNordeaDictionarySuggestionLanguages.map((l) => ({ text: l, value: l }));
}).value;

const resetForm = async () => {
  // Wait for modal transition
  await sleep(150);
  resetFormRaw();
};

const onSubmit = handleSubmit(async (suggestion) => {
  await CompanyDictionarySuggestionsService.sendSuggestion(
    props.dictionaryId,
    props.dictionaryId === DictionariesHelper.DANSKE_BANK_TERMS_DICTIONARY_ID
      ? (suggestion as CompanyDanskeBankDictionarySuggestion)
      : (suggestion as CompanyNordeaDictionarySuggestion)
  );

  onClose(true);
  resetForm();
});

const onClose = (didSubmit = false) => {
  emit("close", didSubmit);
};
</script>

<template>
  <LeModal class="le-company-dictionary-suggestion-modal" :is-open="props.isOpen" @close="onClose()">
    <template #title>
      <LeHeading tag="h2" size="m">{{ suggestTermsDictionaryName }}</LeHeading>
    </template>

    <template #body>
      <div v-if="props.dictionaryId === DictionariesHelper.NORDEA_TERMS_DICTIONARY_ID">
        <p class="le-company-dictionary-suggestion-modal__text mb-0">
          <span class="le-company-dictionary-suggestion-modal__text-bold">{{ suggestTermsDictionaryName }}</span>
          is a living dictionary. To improve and shape it to your needs, we encourage you to:
        </p>

        <ul class="le-company-dictionary-suggestion-modal__text mb-4">
          <li>suggest terms and abbreviations</li>
          <li>add comments in general or to existing terms.</li>
        </ul>

        <p class="le-company-dictionary-suggestion-modal__text">
          Thanks for your valuable input.
          <br />
          Group Translation
        </p>
      </div>

      <form id="suggestion-form" class="le-company-dictionary-suggestion-modal__form" @submit.prevent="onSubmit()">
        <section class="le-company-dictionary-suggestion-modal__form-section mt-2">
          <LeHeading tag="h3" size="s">{{ suggestTermsHeadline }}</LeHeading>
          <LeInput v-model="term" placeholder="Term" />
          <LeInput v-model="example" :placeholder="suggestTermsCommentPlaceholder" />
          <LeSelect
            v-model="source"
            placeholder="Choose source language of term*"
            :options="sourceOptions"
            :has-error="ValidationHelper.displayError(sourceMeta)"
          />
          <small
            class="le-company-dictionary-suggestion-modal__mandatory-indicator"
            :class="{
              'le-company-dictionary-suggestion-modal__mandatory-indicator--error':
                ValidationHelper.displayError(sourceMeta)
            }"
          >
            {{ sourceFieldLabel }}
          </small>
        </section>

        <section class="le-company-dictionary-suggestion-modal__form-section">
          <LeHeading tag="h3" size="s">{{ suggestTermsEmailHeadline }}</LeHeading>
          <LeInput v-model="email" placeholder="Your e-mail*" :has-error="ValidationHelper.displayError(emailMeta)" />
          <small
            class="le-company-dictionary-suggestion-modal__mandatory-indicator"
            :class="{
              'le-company-dictionary-suggestion-modal__mandatory-indicator--error':
                ValidationHelper.displayError(emailMeta)
            }"
          >
            {{ emailFieldLabel }}
          </small>
        </section>
      </form>
    </template>

    <template #buttons>
      <LeButton variant="outline" @click="onClose()">Cancel</LeButton>
      <LeButton type="submit" form="suggestion-form">Send</LeButton>
    </template>
  </LeModal>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all.less";

.le-company-dictionary-suggestion-modal {
  &__text {
    .body-text("m");
  }

  &__text-bold {
    .body-text("m", "semi-bold");
  }

  &__form {
    display: flex;
    flex-direction: column;
  }

  &__form-section {
    display: flex;
    flex-direction: column;

    &:not(:last-of-type) {
      margin-bottom: 24px;
    }

    .le-input-container,
    .le-heading,
    ::v-deep(.le-select) {
      margin-bottom: 12px;
    }
  }

  &__mandatory-indicator {
    .body-text("xs", @style: "italic");
    margin: -8px 0 0 12px;

    &--error {
      color: @colors[@error][600];
    }
  }
}
</style>
